import * as React from 'react';
import { Headlines } from './Headlines';
import { styled, alpha } from '@mui/material/styles';
import { Menu } from '../Common/Utilities/Menu';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import Avatar from '@material-ui/core/Avatar';
import InputBase from '@mui/material/InputBase';
import SearchIcon from '@mui/icons-material/Search';
import Badge from '@mui/material/Badge';
import MenuItem from '@mui/material/MenuItem';
import { apiFetch } from "../../api/apiFetch";
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import NotificationsIcon from '@mui/icons-material/Notifications';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import WidgetsIcon from '@mui/icons-material/Widgets';
import MoreIcon from '@mui/icons-material/MoreVert';
import LogoutIcon from '@mui/icons-material/Logout';
import PropTypes from 'prop-types';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import SmsIcon from '@mui/icons-material/Sms';
import ListItemIcon from '@mui/material/ListItemIcon';
import Typography from '@mui/material/Typography';
import { Divider } from '@mui/material';

// ===== Styled components (IMPORTANT: keep these OUTSIDE the component) =====

const SearchRoot = styled('div', {
    shouldForwardProp: (prop) => prop !== 'focused',
})(({ theme, focused }) => {
    const isDark = theme.palette.mode === "dark";
    const borderBase = alpha(isDark ? theme.palette.common.white : theme.palette.common.black, isDark ? 0.22 : 0.16);
    const borderHover = alpha(isDark ? theme.palette.common.white : theme.palette.common.black, isDark ? 0.30 : 0.22);

    const hoverBg = isDark
        ? alpha(theme.palette.common.black, focused ? 0.55 : 0.22)
        : (focused ? theme.custom.glass.strongSurface : theme.custom.glass.surface);

    const focusBg = isDark
        ? alpha(theme.palette.common.black, 0.45)
        : theme.custom.glass.strongSurface;

    return {
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        height: 36,
        borderRadius: 999,
        width: '100%',
        marginLeft: 8,
        marginRight: 8,

        backgroundColor: focused ? focusBg : 'transparent',
        border: `1px solid ${focused ? borderHover : borderBase}`,

        transition: theme.transitions.create(['background-color', 'border-color'], {
            duration: theme.transitions.duration.short,
        }),

        '&:hover': {
            borderColor: borderHover,
            backgroundColor: hoverBg,
        },

        [theme.breakpoints.up('sm')]: {
            marginLeft: theme.spacing(3),
            width: 'auto',
            minWidth: 260,
        },
    };
});

const SearchIconWrapper = styled('div')(({ theme }) => ({
    padding: theme.spacing(0, 1.5),
    height: '100%',
    position: 'absolute',
    left: 0,
    pointerEvents: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'rgba(245,245,245,0.8)',
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
    width: '100%',
    color: theme.palette.text.secondary,

    '& .MuiInputBase-input': {
        padding: theme.spacing(0.75, 1, 0.75, 0),
        paddingLeft: `calc(1em + ${theme.spacing(4)})`,
        width: '100%',
        fontSize: 14,

        // typed text matches placeholder by design
        color: theme.palette.text.secondary,
        caretColor: theme.palette.primary.main,

        '&::placeholder': {
            color: theme.palette.text.secondary,
            opacity: 1,
        },

        '&:focus': {
            color: theme.palette.text.secondary,
        },
    },

    '& .MuiInputBase-input:focus-visible': {
        outline: 'none',
    },

    '& input:-webkit-autofill': {
        WebkitBoxShadow: `0 0 0 100px ${theme.palette.background.paper} inset`,
        WebkitTextFillColor: theme.palette.text.secondary,
        caretColor: theme.palette.primary.main,
    },
}));
// ===== Component =====

PrimarySearchAppBar.propTypes = {
    user: PropTypes.object,
    setToken: PropTypes.func.isRequired
};

export default function PrimarySearchAppBar({
    user,
    setToken,
    dark,
    setDark,
    handleChatOpen,
    searchCallback,
    handleProfileOpen,
    handleAffiliationsOpen,
    handleActivityOpen,
    handleNotificationsOpen,
    notificationCount,
    handleAccountOpen,
    invitationCount,
    handleInvitationsOpen,
    searchTerm,
    setTopic,
    navIndex,
    appOptions
}) {

    // Constants
    const menuId = 'primary-search-account-menu';
    const mobileMenuId = 'primary-search-account-menu-mobile';

    // State variables
    const [anchorEl, setAnchorEl] = React.useState(null);
    const [mobileMoreAnchorEl, setMobileMoreAnchorEl] = React.useState(null);
    const isMenuOpen = Boolean(anchorEl);
    const isMobileMenuOpen = Boolean(mobileMoreAnchorEl);

    // Search state (CONTROLLED) - prevents repaint/glitch issues
    const [term, setTerm] = React.useState(searchTerm ?? "");
    const [searchFocused, setSearchFocused] = React.useState(false);

    // Keep local term in sync when parent searchTerm changes
    React.useEffect(() => {
        setTerm(searchTerm ?? "");
    }, [searchTerm]);

    // event handlers
    const handleProfileMenuOpen = (event) => {
        setAnchorEl(event.currentTarget);
    };
    const handleMobileMenuClose = () => {
        setMobileMoreAnchorEl(null);
    };
    const handleMenuClose = () => {
        setAnchorEl(null);
        handleMobileMenuClose();
    };
    const handleMobileMenuOpen = (event) => {
        setMobileMoreAnchorEl(event.currentTarget);
    };

    const logoutUser = async () => {
        return apiFetch('account/logout', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            }
        });
    };

    const handleLogout = async () => {
        handleMenuClose();
        await logoutUser();
        setToken('');
        localStorage.clear();
    };

    const handleChat = () => {
        handleMenuClose();
        handleMobileMenuClose();
        handleChatOpen();
    };
    const handleProfile = () => {
        handleMenuClose();
        handleMobileMenuClose();
        handleProfileOpen();
    };
    const handleAffiliations = () => {
        handleMenuClose();
        handleMobileMenuClose();
        handleAffiliationsOpen();
    };
    const handleActivity = () => {
        handleMenuClose();
        handleMobileMenuClose();
        handleActivityOpen();
    };
    const handleAccount = () => {
        handleMenuClose();
        handleMobileMenuClose();
        handleAccountOpen();
    };
    const handleNotification = () => {
        handleMenuClose();
        handleMobileMenuClose();
        handleNotificationsOpen();
    };
    const handleInvitation = () => {
        handleMenuClose();
        handleMobileMenuClose();
        handleInvitationsOpen();
    };
    const handleSetDarkMode = () => {
        handleMenuClose();
        handleMobileMenuClose();
        setDark(!dark);
    };
    const handleSetTopic = () => {
        handleMenuClose();
        setTopic("content");
    };

    // Search handlers
    const runSearch = React.useCallback((value) => {
        // call your parent callback without relying on event object
        if (searchCallback) searchCallback(value);
    }, [searchCallback]);

    const onSearchKeyDown = (event) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            runSearch(term);
        }
        // Optional: Escape clears
        if (event.key === 'Escape') {
            setTerm("");
            runSearch("");
            event.currentTarget.blur();
        }
    };

    // render funcs
    const renderMenu = () => {
        return (
            <Menu
                dark={dark}
                width={1000}
                anchorEl={anchorEl}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                id={menuId}
                keepMounted
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                open={isMenuOpen}
                onClose={handleMenuClose}
            >
                <MenuItem>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={dark}
                                color="secondary"
                                onChange={handleSetDarkMode}
                                size="small"
                                inputProps={{ 'aria-label': 'controlled' }}
                            />
                        }
                        label={<Typography style={{ paddingLeft: "24px" }}>Dark Mode</Typography>}
                    />
                </MenuItem>

                <Divider />

                {notificationCount > 0 &&
                    <MenuItem onClick={handleNotification} sx={{ display: { xs: "none", lg: "flex" } }}>
                        <ListItemIcon>
                            <Badge badgeContent={notificationCount ?? null} color="error">
                                <NotificationsIcon />
                            </Badge>
                        </ListItemIcon>
                        <Typography variant="inherit">
                            {notificationCount === 0 ? "No Notifications" : notificationCount + " Notifications"}
                        </Typography>
                    </MenuItem>
                }

                {(user) &&
                    <MenuItem onClick={handleChat} sx={{ display: { xs: "none", lg: "flex" } }}>
                        <ListItemIcon>
                            <Badge invisible={!user.hasNewMessages} color="info" variant="dot">
                                <SmsIcon fontSize="small" />
                            </Badge>
                        </ListItemIcon>
                        <Typography variant="inherit">{user.hasNewMessages ? "New Messages" : "Conversations"}</Typography>
                    </MenuItem>
                }

                {navIndex !== 1 &&
                    <MenuItem onClick={handleSetTopic} sx={{ display: { xs: "none", lg: "flex" } }}>
                        <ListItemIcon>
                            <WidgetsIcon fontSize="small" />
                        </ListItemIcon>
                        <Typography variant="inherit">My Content</Typography>
                    </MenuItem>
                }

                <MenuItem onClick={handleProfile} sx={{ display: { xs: "none", lg: "flex" } }}>
                    <ListItemIcon>
                        <AccountCircleIcon fontSize="small" />
                    </ListItemIcon>
                    <Typography variant="inherit">Profile</Typography>
                </MenuItem>

                <MenuItem onClick={handleAccount}>
                    <ListItemIcon>
                        <ManageAccountsIcon fontSize="small" />
                    </ListItemIcon>
                    <Typography variant="inherit">Account</Typography>
                </MenuItem>

                <MenuItem onClick={handleLogout}>
                    <ListItemIcon>
                        <LogoutIcon fontSize="small" />
                    </ListItemIcon>
                    <Typography variant="inherit">Sign Out</Typography>
                </MenuItem>
            </Menu>
        );
    };

    const renderMobileMenu = () => {
        return (
            <Menu
                dark={dark}
                width={500}
                anchorEl={mobileMoreAnchorEl}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                id={mobileMenuId}
                keepMounted
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                open={isMobileMenuOpen}
                onClose={handleMobileMenuClose}
            >
                <MenuItem>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={dark}
                                color="secondary"
                                onChange={handleSetDarkMode}
                                size="small"
                                inputProps={{ 'aria-label': 'controlled' }}
                            />
                        }
                        label=" Dark Mode"
                    />
                </MenuItem>

                {(notificationCount > 0) &&
                    <MenuItem onClick={handleNotification} sx={{ display: { xs: "none", lg: "flex" } }}>
                        <ListItemIcon>
                            <Badge badgeContent={notificationCount ?? null} color="error">
                                <NotificationsIcon />
                            </Badge>
                        </ListItemIcon>
                        <Typography variant="inherit">
                            {notificationCount === 0 ? "No Notifications" : notificationCount + " Notifications"}
                        </Typography>
                    </MenuItem>
                }

                {(user) &&
                    <MenuItem onClick={handleChat} sx={{ display: { xs: "none", lg: "flex" } }}>
                        <ListItemIcon>
                            <Badge invisible={!user.hasNewMessages} color="secondary" variant="dot">
                                <SmsIcon fontSize="small" />
                            </Badge>
                        </ListItemIcon>
                        <Typography variant="inherit">{user.hasNewMessages ? "New Messages" : "Conversations"}</Typography>
                    </MenuItem>
                }

                {navIndex !== 1 &&
                    <MenuItem onClick={handleSetTopic} sx={{ display: { xs: "none", lg: "flex" } }}>
                        <ListItemIcon>
                            <WidgetsIcon fontSize="small" />
                        </ListItemIcon>
                        <Typography variant="inherit">My Content</Typography>
                    </MenuItem>
                }

                <MenuItem onClick={handleProfile} sx={{ display: { xs: "none", lg: "flex" } }}>
                    <ListItemIcon>
                        <AccountCircleIcon fontSize="small" />
                    </ListItemIcon>
                    <Typography variant="inherit">Profile</Typography>
                </MenuItem>

                <MenuItem onClick={handleAccount}>
                    <ListItemIcon>
                        <ManageAccountsIcon fontSize="small" />
                    </ListItemIcon>
                    <Typography variant="inherit">Account</Typography>
                </MenuItem>

                <MenuItem onClick={handleLogout}>
                    <ListItemIcon>
                        <LogoutIcon fontSize="small" />
                    </ListItemIcon>
                    <Typography variant="inherit">Sign Out</Typography>
                </MenuItem>
            </Menu>
        );
    };

    const renderAvatar = () => {
        return (
            <Badge
                invisible={!user.hasNewMessages && !notificationCount}
                color="info"
                variant="dot"
                anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                overlap="circular"
            >
                <Avatar className="avatarImageMedium" alt={user.firstName} src={"data:image/png;base64, " + user.avatar} />
            </Badge>
        );
    };

    // ✅ Hide logo on mobile entirely by setting xs to 'none'
    const logo = () => {
        return (
            <>
                <Button
                    color="secondary"
                    sx={{
                        p: 0,
                        display: { xs: 'none', sm: 'block' },
                        marginRight: "10px",
                        cursor: "pointer",
                        filter: "brightness(0.1) invert(1)"
                    }}
                    onClick={() => setTopic("posts")}
                >
                    <img src={dark ? '/Images/lsletters.png' : '/Images/lsletters.png'} height="32px" widthtitle="LiveSphere" />
                </Button>

                {/* Removed the xs logo button entirely */}
            </>
        );
    };

    const searchBox = () => {
        return (
            <SearchRoot focused={searchFocused} darkmode={dark ? 1 : 0}>
                <SearchIconWrapper>
                    <SearchIcon fontSize="small" />
                </SearchIconWrapper>

                <StyledInputBase
                    value={term}
                    onChange={(e) => setTerm(e.target.value)}
                    onKeyDown={onSearchKeyDown}
                    onFocus={() => setSearchFocused(true)}
                    onBlur={() => setSearchFocused(false)}
                    placeholder="Search LiveSphere"
                    inputProps={{ 'aria-label': 'search' }}
                />
            </SearchRoot>
        );
    };

    const gapSpacer = () => <Box sx={{ flexGrow: 1 }} />;

    const headlines = () => <Headlines dark={dark} user={user} />;

    const userIconButton = () => {
        return (
            <>
                <Box sx={{ display: { xs: 'none', md: 'flex' } }}>
                    <IconButton
                        size="medium"
                        edge="end"
                        aria-label="account of current user"
                        aria-controls={menuId}
                        aria-haspopup="true"
                        onClick={handleProfileMenuOpen}
                        sx={{ color: 'text.primary' }}
                    >
                        {user ? renderAvatar() : <AccountCircleIcon />}
                    </IconButton>
                </Box>

                <Box sx={{ display: { xs: 'flex', md: 'none' } }}>
                    <Tooltip title="User Options">
                        <IconButton
                            size="large"
                            aria-label="show more"
                            aria-controls={mobileMenuId}
                            aria-haspopup="true"
                            onClick={handleMobileMenuOpen}
                            color="inherit"
                            sx={{ p: { xs: 0, sm: undefined }, mr: { xs: 1, sm: undefined } }}
                        >
                            <MoreIcon />
                        </IconButton>
                    </Tooltip>
                </Box>
            </>
        );
    };

    return (
        <AppBar
            elevation={0}
            position="fixed"
            sx={{
                bgcolor: { xs: "transparent", sm: "background.paper" },
                color: "text.primary",
                boxShadow: { xs: "none", sm: 1 },

                "@media (max-width:600px)": {
                    background: "transparent",
                    boxShadow: "none",
                    backdropFilter: "blur(1px)",
                    WebkitBackdropFilter: "blur(1px)",
                },
            }}
        >
            <Toolbar sx={{ pr: { xs: 0, sm: 2 } }}>
                {logo()}
                {searchBox()}
                {gapSpacer()}
                {userIconButton()}
            </Toolbar>

            {renderMobileMenu()}
            {renderMenu()}
        </AppBar>
    );
}


